import React, { createContext, useContext } from 'react';
import { node } from 'prop-types';
import { useHistory } from 'react-router-dom';
import AUIButton from '@ancert/aui-button';
import AUIIcon from '@ancert/aui-icon';
import { useNotification } from '@ancert/aui-notification-context';
import { useSession } from './session.context';
import { postLogout } from '../services/user.service';
import { ROUTES } from '../constants';
import { ROL, ROLES } from '../lib/role.enum';

const SessionBarContext = createContext(null);

/**
 * Componente que genera el Layout
 *
 * @component
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
const SessionBarProvider = ({ children }) => {
  const history = useHistory();
  const { openNotification } = useNotification();
  const { getUser } = useSession();
  const { name, lastname1, lastname2, dni, role } = getUser();

  const getUserName = () => {
    if (!role) {
      return '';
    }
    if (role !== ROL.DENUNCIANTE) {
      return ROLES.find((rol) => rol.id === role).desc;
    }

    if (!name) {
      return 'Anónimo';
    }

    return `${name} ${lastname1} ${lastname2 || ''}${dni ? `- ${dni}` : ''}`;
  };

  const onLogout = async () => {
    const { status, error } = await postLogout();
    if (status === 200) {
      sessionStorage.clear();
      history.push(ROUTES.HOME);
      return;
    }
    const { message } = error;
    openNotification({
      title: 'Error',
      content: <div>{message || 'Ha ocurrido intentando salir de la aplicación.'}</div>,
      type: 'error',
      centerButtons: true,
    });
  };

  return (
    <SessionBarContext.Provider value={null}>
      <div className="c-sii-session-bar">
        <span className="c-sii-session-bar__user">
          <AUIIcon color="blue" icon="userOutline" />
          {getUserName()}
        </span>
        <AUIButton
          className="c-sii-session-bar__logout"
          iconPos="left"
          icon="logout"
          label="Cerrar sesión"
          mode="ghost"
          onClick={onLogout}
        />
      </div>
      {children}
    </SessionBarContext.Provider>
  );
};

SessionBarProvider.propTypes = {
  children: node.isRequired,
};

const useSessionBar = () => {
  const context = useContext(SessionBarContext);

  if (context === undefined) {
    throw new Error('useSessionBar must be used within provider');
  }

  return context;
};

export { SessionBarProvider, SessionBarContext, useSessionBar };
